import React, { Fragment } from 'react';
import Copyright from '@gravity/copyright';
import { FormattedHTMLMessage } from 'react-intl';
import { ConfigType } from '@nucleo/config';
import Link from '@gravity/link';
import styled from 'styled-components';
import StylingConstants from '@gravity/styling-constants';
import iframeResizer from './util/iframeResizer';

const Wrapper = styled.footer.attrs({
  className: 'stx-tsFooter',
})`
  box-sizing: border-box;
  position: relative;
  font-size: ${StylingConstants.font.sizeXs};
  font-weight: bold;
  text-transform: uppercase;
  margin-top: auto;
  background: ${props => (props.theme.tsFooter && props.theme.tsFooter.backgroundImage) || 'none'};
  background-color: ${props =>
    (props.theme.tsFooter && props.theme.tsFooter.backgroundColor) || '#fff'};
`;

const FooterWrapper = styled.div.attrs({
  className: 'stx-footerWrapper'
})`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  max-width: 80rem;
  margin: 0 auto;
  padding: 1rem 2rem;
  min-height: 3.25rem;
  color: ${props =>
  props.theme.tsFooter && props.theme.tsFooter.color ? props.theme.tsFooter.color : '#000'};
`;

const FooterInner = styled.ul.attrs({
  className: 'stx-tsFooterInner',
})`
  padding: 0;
  display: contents;
`;

const FooterLinkWrapper = styled.li.attrs({
  className: 'stx-tsFooterLinkWrapper',
})`
  list-style: none;
`;

const FooterLink = styled(Link).attrs({
  className: 'stx-tsFooterLink',
})`
  color: ${props => props.theme.tsFooter && props.theme.tsFooter.color};
  margin: 0;

  &::before {
    content: ' | ';
    margin: 0 0.5rem;
  }

  &:focus,
  &:hover,
  &:active {
    color: ${props => props.theme.tsFooter && props.theme.tsFooter.color};
  }
`;

/**
 * Get the target of the event and resize the iframe according to the body
 * @param {Event} evt, load event
 */
const handleCustomFooterOnLoad = evt => {
  const iframe = evt.target;
  iframeResizer(iframe);
};

/**
 * Check if the URL passed by params contains a placeholder to be
 * replaced by the language
 *
 * @param {string} url, url to be replaced
 * @param {string} language, language to add inside the url
 *
 * @returns string with the url parsed
 */
const replaceLanguagePlaceHolder = (url, language) => {
  const LANGUAGE_CODE_PLACE_HOLDER = '{LANGUAGE}';
  if (url && url.indexOf(LANGUAGE_CODE_PLACE_HOLDER) >= 0) {
    return url.replace(LANGUAGE_CODE_PLACE_HOLDER, language);
  }
  return url;
};

/**
 * Render a component footer or a custom footer if 'hasCustomFooter' param is true
 *
 * @param {object} config, object with the configuration of the site
 */
const Footer = props => {
  const {
    contactEmail,
    generalConditionsURL,
    helpURL,
    contactURL,
    baseUrl,
    currentLanguage,
    privacyPolicyURL,
    terminateSubscriptionURL,
  } = props.config.configuration;
  const style = props.config.style.properties;
  const language = props.config.language;
  const hasCustomFooter = style && style.hasCustomFooter;
  return (
    <Wrapper>
      {hasCustomFooter && (
        <iframe
          title="stx-custom-footer"
          id="stx-custom-footer"
          src={`${baseUrl}/api/1/resources/custom/${currentLanguage}/footer.html`}
          height="100%"
          width="100%"
          frameBorder="0"
          scrolling="no"
          onLoad={handleCustomFooterOnLoad}
        />
      )}
      {!hasCustomFooter && (
        <FooterWrapper>
          <Copyright holder="SecuTix" />
          <FooterInner>
            <Fragment>

              <FooterLinkWrapper>
                <FooterLink href="https://www.secutix.com" target="_blank">
                  <FormattedHTMLMessage id="gravity.TsFooter.createdBy" values={{ name: 'SecuTix' }} />
                </FooterLink>
              </FooterLinkWrapper>
              <FooterLinkWrapper>
                <FooterLink href={`${baseUrl}sitemap`} target="_blank">
                  <FormattedHTMLMessage id="gravity.TsFooter.siteMap" />
                </FooterLink>
              </FooterLinkWrapper>
            </Fragment>
            <Fragment>
              {contactEmail && (
                <FooterLinkWrapper>
                  <FooterLink href={`mailto:${contactEmail}`}>{contactEmail}</FooterLink>
                </FooterLinkWrapper>
              )}
              {generalConditionsURL && (
                <FooterLinkWrapper>
                  <FooterLink
                    href={replaceLanguagePlaceHolder(generalConditionsURL, language || currentLanguage)}
                    target="_blank">
                    <FormattedHTMLMessage id="gravity.TsFooter.generalConditions" />
                  </FooterLink>
                </FooterLinkWrapper>
              )}
              {privacyPolicyURL && (
                <FooterLinkWrapper>
                  <FooterLink
                    href={replaceLanguagePlaceHolder(privacyPolicyURL, language || currentLanguage)}
                    target="_blank">
                    <FormattedHTMLMessage id="gravity.TsFooter.privacyPolicy" />
                  </FooterLink>
                </FooterLinkWrapper>
              )}
              {helpURL && (
                <FooterLinkWrapper>
                  <FooterLink
                    href={replaceLanguagePlaceHolder(helpURL, language || currentLanguage)}
                    target="_blank">
                    <FormattedHTMLMessage id="gravity.TsFooter.help" />
                  </FooterLink>
                </FooterLinkWrapper>
              )}
              {contactURL && (
                <FooterLinkWrapper>
                  <FooterLink
                    href={replaceLanguagePlaceHolder(contactURL, language || currentLanguage)}
                    target="_blank">
                    <FormattedHTMLMessage id="gravity.TsFooter.contactUs" />
                  </FooterLink>
                </FooterLinkWrapper>
              )}
              {terminateSubscriptionURL && (
                <FooterLinkWrapper>
                  <FooterLink
                    href={replaceLanguagePlaceHolder(terminateSubscriptionURL, language || currentLanguage)}
                    target="_blank">
                    <FormattedHTMLMessage id="gravity.TsFooter.termination" />
                  </FooterLink>
                </FooterLinkWrapper>
              )}
            </Fragment>
          </FooterInner>
        </FooterWrapper>
      )}
    </Wrapper>
  );
};

Footer.propTypes = {
  /** config Object provided by the nucleo */
  config: ConfigType.isRequired,
};

Footer.defaultProps = {
  config: null,
};

export default Footer;
